import React from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { IconLogout, IconMenu, IconPlus } from "../ui/icons";
import { adminBottomNav, adminNavSections, operatorBottomNav, ROUTES } from "./navConfig";

const ITEM_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  minHeight: 48,
  cursor: "pointer",
  border: "none",
  background: "none",
  padding: 0,
  fontFamily: "inherit",
};

/**
 * Barra inferior de mobile: 4 destinos y el botón central de nueva orden.
 * El botón sobresale del borde superior, pero la barra reserva su alto para
 * que no se recorte contra el borde.
 */
const BottomNav = ({ isAdmin, onLogout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = React.useState(false);

  const items = isAdmin ? adminBottomNav : operatorBottomNav;
  const [first, second, third] = items;

  const go = (path) => {
    localStorage.removeItem("signatureData");
    navigate(path);
    setMoreOpen(false);
  };

  const NavItem = ({ item }) => {
    const active = item.path === pathname;
    const Icon = item.icon;
    return (
      <Box
        component="button"
        type="button"
        onClick={() => go(item.path)}
        aria-current={active ? "page" : undefined}
        sx={{ ...ITEM_SX, color: active ? tokens.brand : tokens.ink3 }}
      >
        <Icon size={21} strokeWidth={active ? 1.7 : 1.6} />
        <Typography sx={{ fontSize: "10.5px", fontWeight: active ? 600 : 500 }}>
          {item.label}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box
        component="nav"
        aria-label="Navegación"
        sx={{
          // Fija y por encima de todo: el botón + no puede quedar detrás de nada.
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1250,
          height: tokens.bottomNavHeight,
          backgroundColor: tokens.surface,
          borderTop: `1px solid ${tokens.border}`,
          boxShadow: "0 -1px 12px rgba(28,26,25,0.06)",
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          alignItems: "center",
          padding: "0 4px",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <NavItem item={first} />
        <NavItem item={second} />

        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", height: "100%" }}>
          <Box
            component="button"
            type="button"
            aria-label="Nueva orden"
            onClick={() => go(ROUTES.newOrder)}
            sx={{
              width: 52,
              height: 52,
              marginTop: "-18px",
              borderRadius: 999,
              border: `3px solid ${tokens.surface}`,
              backgroundColor: tokens.brand,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: tokens.shadowBrand,
              cursor: "pointer",
              padding: 0,
              position: "relative",
              zIndex: 2,
            }}
          >
            <IconPlus size={24} />
          </Box>
        </Box>

        <NavItem item={third} />

        {isAdmin ? (
          <Box
            component="button"
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-label="Más secciones"
            sx={{ ...ITEM_SX, color: tokens.ink3 }}
          >
            <IconMenu size={21} />
            <Typography sx={{ fontSize: "10.5px", fontWeight: 500 }}>Más</Typography>
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={onLogout}
            aria-label="Cerrar sesión"
            sx={{ ...ITEM_SX, color: tokens.ink3 }}
          >
            <IconLogout size={21} />
            <Typography sx={{ fontSize: "10.5px", fontWeight: 500 }}>Salir</Typography>
          </Box>
        )}
      </Box>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: tokens.surface,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "80vh",
            padding: "8px 0 max(16px, env(safe-area-inset-bottom))",
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 999,
            backgroundColor: tokens.borderStrong,
            margin: "6px auto 10px",
          }}
        />
        {adminNavSections.map((section) => (
          <Box key={section.label} sx={{ paddingBottom: "6px" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: tokens.ink3,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "10px 20px 4px",
              }}
            >
              {section.label}
            </Typography>
            {section.items.flatMap((item) =>
              item.children
                ? item.children.map((child) => (
                    <Box
                      key={child.path}
                      onClick={() => go(child.path)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        minHeight: 48,
                        padding: "0 20px",
                        fontSize: "14.5px",
                        cursor: "pointer",
                        color: child.path === pathname ? tokens.brandDark : tokens.ink,
                        fontWeight: child.path === pathname ? 500 : 400,
                      }}
                    >
                      <item.icon size={19} />
                      {item.label} · {child.label}
                    </Box>
                  ))
                : [
                    <Box
                      key={item.path}
                      onClick={() => go(item.path)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        minHeight: 48,
                        padding: "0 20px",
                        fontSize: "14.5px",
                        cursor: "pointer",
                        color: item.path === pathname ? tokens.brandDark : tokens.ink,
                        fontWeight: item.path === pathname ? 500 : 400,
                      }}
                    >
                      <item.icon size={19} />
                      {item.label}
                    </Box>,
                  ]
            )}
          </Box>
        ))}

        <Box
          onClick={onLogout}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minHeight: 48,
            padding: "0 20px",
            fontSize: "14.5px",
            cursor: "pointer",
            color: tokens.brandDark,
            borderTop: `1px solid ${tokens.borderSoft}`,
            marginTop: "6px",
          }}
        >
          <IconLogout size={19} />
          Cerrar sesión
        </Box>
      </Drawer>
    </>
  );
};

export default BottomNav;
